"use client";

import { useEffect, useState } from "react";
import { DashboardStats } from "@/types";
import { DashboardAPI } from "@/lib/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  BarChart,
  Users,
  ShieldAlert,
  AlertTriangle,
  MessageSquare,
  UserCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

export default function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats>({
    totalCitizens: 0,
    totalUsers: 0,
    totalOpinions: 0,
    totalCitizenRequest: 0,
    pendingCitizenRequest: 0,
    totalReports: 0,
    pendingReports: 0,
    totalContactUs: 0,
    pendingContactUs: 0,
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await DashboardAPI.getStats();
        setStats(data);
      } catch (error) {
        console.error("Failed to fetch dashboard stats:", error);
      }
    };
    fetchStats();
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground">
          Welcome to the Citizen Nexus admin panel.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card className="stat-card">
          <div className="rounded-full bg-primary/10 p-2">
            <Users className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-medium">{stats.totalCitizens}</h3>
            <p className="text-sm text-muted-foreground">Total Citizens</p>
          </div>
        </Card>

        <Card className="stat-card">
          <div className="rounded-full bg-primary/10 p-2">
            <UserCircle className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-medium">{stats.totalUsers}</h3>
            <p className="text-sm text-muted-foreground">Total Users</p>
          </div>
        </Card>

        <Card className="stat-card">
          <div className="rounded-full bg-primary/10 p-2">
            <MessageSquare className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-medium">{stats.totalOpinions}</h3>
            <p className="text-sm text-muted-foreground">Total Opinions</p>
          </div>
        </Card>

        <Card className="stat-card">
          <div className="rounded-full bg-secondary/10 p-2">
            <BarChart className="h-5 w-5 text-secondary" />
          </div>
          <div>
            <h3 className="text-lg font-medium">
              {stats.pendingCitizenRequest}{" "}
              <span className="text-sm">(pending)</span>
            </h3>
            <p className="text-sm text-muted-foreground">
              Citizen Requests
              <br />
              <span className="text-xs">
                Total: {stats.totalCitizenRequest}
              </span>
            </p>
          </div>
        </Card>

        <Card className="stat-card">
          <div className="rounded-full bg-warning/10 p-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
          </div>
          <div>
            <h3 className="text-lg font-medium">
              {stats.pendingReports} <span className="text-sm">(pending)</span>
            </h3>
            <p className="text-sm text-muted-foreground">
              Report Abuse
              <br />
              <span className="text-xs">Total: {stats.totalReports}</span>
            </p>
          </div>
        </Card>

        <Card className="stat-card">
          <div className="rounded-full bg-destructive/10 p-2">
            <ShieldAlert className="h-5 w-5 text-destructive" />
          </div>
          <div>
            <h3 className="text-lg font-medium">
              {stats.pendingContactUs}{" "}
              <span className="text-sm">(pending)</span>
            </h3>
            <p className="text-sm text-muted-foreground">
              Contact Us
              <br />
              <span className="text-xs">Total: {stats.totalContactUs}</span>
            </p>
          </div>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="activity">Recent Activity</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
              <CardDescription>Common tasks and actions</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
              <Button
                variant="outline"
                asChild
                className="h-32 flex-col justify-center gap-2"
              >
                <Link to="/citizens/new">
                  <Users className="h-8 w-8 text-primary" />
                  <span>Add New Citizen</span>
                </Link>
              </Button>

              <Button
                variant="outline"
                asChild
                className="h-32 flex-col justify-center gap-2"
              >
                <Link to="/reports?status=pending">
                  <AlertTriangle className="h-8 w-8 text-warning" />
                  <span>Review Pending Reports</span>
                </Link>
              </Button>

              <Button
                variant="outline"
                asChild
                className="h-32 flex-col justify-center gap-2"
              >
                <Link to="/requests">
                  <ShieldAlert className="h-8 w-8 text-accent" />
                  <span>Approve Citizen Requests</span>
                </Link>
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="activity">
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>Latest actions and updates</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                No recent activity to display.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
